import { useContext } from 'react';
import { DiscordContext } from '../discord/DiscordContext';

const SNOWFLAKE_RE = /^discord_\d+/;

/**
 * True when `value` is the synthetic `discord_<digits>` Django
 * username HSH mints for Discord-flavored accounts. Exported so the
 * seat-capture chain (dispatchTakeSeat / SeatRow) guards against the
 * snowflake with the exact same predicate — one definition, no drift.
 *
 * @param {*} value candidate name.
 * @returns {boolean}
 */
export function isSnowflakeUsername(value) {
    return typeof value === 'string' && SNOWFLAKE_RE.test(value);
}

/**
 * Resolve the local user's render name from an `app` reference plus a
 * Discord context slice. Pure (no hooks) so class components can call
 * it on each render; the `useLocalDisplayName` hook wraps it for
 * functional callers.
 *
 * Precedence (skip any candidate matching the snowflake shape
 * `discord_<digits>`, the synthetic Django username we generate for
 * Discord-flavored accounts):
 *
 *   1. Per-server nick from the guild this Activity launched into
 *   2. Discord global_name
 *   3. Discord @handle (provider_username on SocialAccount)
 *   4. identity.display_name (server-resolved precedence chain;
 *      mirrors LobbyUserSerializer.get_display_name)
 *   5. account.player_name (HSH-side custom)
 *   6. '' — never the snowflake
 *
 * @param {object|null} app top-level App component (`this.props.app`).
 * @param {object|null} discord Discord context slice.
 * @returns {string} the resolved display name, or '' when no candidate
 *   survives the snowflake guard.
 */
export function getLocalDisplayName(app, discord) {
    const account = app && app.state && app.state.authenticator
        && app.state.authenticator.account;
    const identity = account && account.user;
    const candidates = [
        discord && discord.discordNick,
        discord && discord.discordGlobalName,
        discord && discord.discordUsername,
        identity && identity.display_name,
        account && account.player_name,
    ];
    for (const candidate of candidates) {
        if (candidate && !isSnowflakeUsername(candidate)) {
            return candidate;
        }
    }
    return '';
}

/**
 * Canonical local-user display name for functional callers.
 *
 * Reads the Discord context off `DiscordContext` and delegates to
 * `getLocalDisplayName`. `app` is the top-level App component
 * (`this.props.app`) for legacy class-component callers; pass null
 * from functional callers that already destructure identity elsewhere.
 *
 * @param {object|null} app top-level App component.
 * @returns {string} the resolved display name (never the snowflake).
 */
export function useLocalDisplayName(app) {
    const discord = useContext(DiscordContext);
    return getLocalDisplayName(app, discord);
}

export const _SNOWFLAKE_RE_FOR_TESTING = SNOWFLAKE_RE;
